import { useEffect, useState, type FormEvent } from "react";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Label } from "../../components/ui/Label";
import { Textarea } from "../../components/ui/Textarea";
import { api, ApiException } from "../../lib/api";

interface CircleResponse {
  circle: { id: string; name: string };
  loved_one: { id: string; name: string; date_of_birth: string | null; address: string | null; notes: string | null } | null;
  role: "admin" | "member";
}

export function LovedOnePanel({ circleId }: { circleId: string }) {
  const [data, setData] = useState<CircleResponse | null>(null);
  const [name, setName] = useState("");
  const [dob, setDob] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<CircleResponse>(`/api/circles/${circleId}`)
      .then((d) => {
        setData(d);
        if (d.loved_one) {
          setName(d.loved_one.name);
          setDob(d.loved_one.date_of_birth ?? "");
          setAddress(d.loved_one.address ?? "");
          setNotes(d.loved_one.notes ?? "");
        }
      })
      .catch(() => {});
  }, [circleId]);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await api.post(`/api/circles/${circleId}`, {
        loved_one_name: name,
        loved_one_dob: dob || null,
        loved_one_address: address || null,
        loved_one_notes: notes || null,
      });
      setInfo("Saved.");
    } catch (err) {
      setError(err instanceof ApiException ? err.message : "Save failed.");
    } finally {
      setBusy(false);
    }
  }

  if (!data) return <div className="text-text-muted">Loading…</div>;

  return (
    <div>
      <h2 className="text-lg font-semibold">Loved one</h2>
      <p className="mt-1 text-sm text-text-muted">Details visible to every circle member.</p>

      <form onSubmit={submit} className="mt-5 space-y-4">
        <div>
          <Label htmlFor="lo-name">Name</Label>
          <Input id="lo-name" required value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="lo-dob">Date of birth</Label>
          <Input id="lo-dob" type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="lo-addr">Home address</Label>
          <Textarea id="lo-addr" rows={2} value={address} onChange={(e) => setAddress(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="lo-notes">Notes</Label>
          <Textarea id="lo-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>

        {info && <div className="rounded-md bg-green-50 border border-green-200 text-success text-sm px-3 py-2">{info}</div>}
        {error && <div className="rounded-md bg-red-50 border border-red-200 text-danger text-sm px-3 py-2">{error}</div>}

        <div className="pt-2">
          <Button type="submit" loading={busy}>Save changes</Button>
        </div>
      </form>
    </div>
  );
}
